import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import useReveal from "@/hooks/useReveal";
import MagneticButton from "@/components/ui/MagneticButton";
import HeroSwitcher from "@/components/HeroSwitcher";
import SEO from "@/components/SEO";
import PartnerLogos from "@/components/ui/PartnerLogos";

import SystemBoot from "@/components/ui/SystemBoot";
import SystemWidget from "@/components/hero/SystemWidget";
import TextReveal from "@/components/ui/TextReveal";

export default function HomePage() {
  useReveal('.reveal');
  const [booted, setBooted] = useState(false);
  const [showBoot, setShowBoot] = useState(true);

  useEffect(() => {
    const hasBooted = sessionStorage.getItem('nily_booted');
    if (hasBooted) {
      setShowBoot(false);
      setBooted(true);
    }
  }, []);

  const handleBootComplete = () => {
    sessionStorage.setItem('nily_booted', 'true');
    setBooted(true);
    setTimeout(() => setShowBoot(false), 500);
  };

  const faqs = [
    { question: "Is my data actually private?", answer: "Yes. Nily uses a 'Zero-Knowledge' architecture for personal entries. Your employer or school sees only aggregated trends, never individual data." },
    { question: "Does this replace therapy?", answer: "No. Nily is an OS for daily maintenance and early signal detection. It integrates with clinical care but does not replace crisis management." },
    { question: "How does pricing work?", answer: "Nily is free for individual beta users. For Workplaces and Campuses, we offer per-seat licensing significantly cheaper than traditional EAP." },
    { question: "Is it clinically validated?", answer: "Yes. Our Neurova engine is built on PSS-4, GAD-7, and PHQ-9 scales, currently mapping to SaMD regulatory standards." }
  ];

  return (
    <>
      <SEO title="Nily | The Mental Health OS" description="One intelligent operating system for mental wellbeing." />
      {showBoot && <SystemBoot onComplete={handleBootComplete} />}

      <div className={`relative transition-opacity duration-1000 ${booted ? 'opacity-100' : 'opacity-0'}`}>
        <section className="relative min-h-[95vh] flex flex-col items-center justify-center pt-32 pb-20 px-6 overflow-hidden">
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[1000px] h-[400px] bg-[var(--nily-teal)]/10 blur-[120px] rounded-full pointer-events-none" />

          <div className="relative z-10 text-center max-w-4xl mx-auto space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[var(--glass-border)] bg-[var(--glass-bg)] backdrop-blur-md">
                <span className="w-2 h-2 rounded-full bg-[var(--safe-emerald)] animate-pulse" />
                <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">System Online v1.0</span>
              </div>

              <h1 className="font-heading text-6xl md:text-8xl leading-[0.95] text-[var(--text-primary)] tracking-tight">
                <TextReveal text="One intelligent layer" />
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--nily-teal)] to-[var(--neurova-purple)]">
                  for your human mind.
                </span>
              </h1>

              <p className="text-[var(--text-muted)] text-xl max-w-2xl mx-auto leading-relaxed">
                Nily unifies clinical diagnostics, workplace analytics, and daily wellbeing into a single operating system.
              </p>
            </div>

            <div className="flex flex-col md:flex-row items-center justify-center gap-6 pt-4">
              <Link to="/beta">
                <MagneticButton className="px-10 py-4 text-lg font-bold bg-[var(--nily-teal)] text-[var(--bg-void)]">
                  Join Private Beta
                </MagneticButton>
              </Link>
              <Link to="/science" className="group flex items-center gap-2 text-[var(--text-primary)] font-medium hover:text-[var(--nily-teal)] transition-colors">
                Explore the Science
                <span className="group-hover:translate-x-1 transition-transform">→</span>
              </Link>
            </div>

            <div className="pt-12 flex justify-center">
               <SystemWidget />
            </div>
          </div>
        </section>

        <section className="border-y border-[var(--glass-border)] bg-[var(--glass-bg)]/50 backdrop-blur-sm py-10">
           <div className="mx-auto max-w-7xl px-6 flex flex-col md:flex-row items-center gap-8 justify-center">
              <p className="text-xs font-mono text-[var(--text-muted)] uppercase tracking-widest">Engineered with Clinicians from</p>
              <PartnerLogos />
           </div>
        </section>

        <section className="py-32 relative z-20">
           <div className="text-center mb-16">
              <h2 className="font-heading text-3xl text-[var(--text-primary)]">Choose your interface.</h2>
           </div>
           <HeroSwitcher />
        </section>

        <section className="py-24 bg-[var(--bg-void)] relative z-20">
           <div className="mx-auto max-w-3xl px-6">
              <h2 className="font-heading text-3xl text-[var(--text-primary)] mb-10 text-center">Common Queries</h2>
              
           </div>
        </section>

        <section className="py-40 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--nily-teal)]/10 to-transparent pointer-events-none" />
          <div className="relative z-10 mx-auto max-w-4xl px-6 space-y-8">
            <h3 className="font-heading text-5xl md:text-7xl text-[var(--text-primary)] tracking-tight">Ready to upgrade?</h3>
            <div className="flex justify-center pt-4">
              <Link to="/beta"><MagneticButton className="px-12 py-5 text-lg bg-[var(--nily-teal)] text-[var(--bg-void)]">Join Private Beta</MagneticButton></Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
